import { Component } from 'react'
import PropTypes from 'prop-types'
import { bowserFlags } from 'lib/bowser'
import { has, map } from 'lodash'
import fetch from 'lib/fetch'
import { parseErrors } from 'lib/api-utils'
import Scroll from 'react-scroll/modules'
import { SUPPORT_EMAIL, SYSTEM_ERROR_KEY } from 'shared/constants'
import { Form, Input, Radio, Select } from 'components/forms'
import { InfoButton } from 'components/buttons'
import { Modal, ModalBody } from 'components/modal'
import Image from 'components/image'
import { track, withContextualizedTracking } from 'lib/analytics'
import Link from 'components/link'
import withCurrentUser from 'shared/HOC/withCurrentUser'
import { AsyncPoller } from 'components/async'
import {
  numeric as validateRegistryNumber,
  url as validateUrl,
} from 'lib/form-validation'

import * as blUrls from 'lib/urls'
import { apiV3ExternalRegistrySupportTicketPath } from 'lib/urls'
import onCurrentUserLoaded from 'shared/utils/onCurrentUserLoaded/onCurrentUserLoaded'
import { VARIANTS } from 'components/alerts'
import { DangerIcon, InfoIcon, SuccessIcon } from 'components/icons'
import css from './add-other-registry.scss'

const TRANSFER = 'transfer'
const LINK = 'link'

const STORE_HINT_CLOUDINARY_IMAGE_IDS = {
  // TODO: Temporarily disabling as these images are out of date
  // we need to update these images or rework the link help pattern before we re-enable this.
  //
  // walmart: ['external_registry_hint_walmart_wdrwwf'],
  // target: ['external_registry_hint_target_yzs8uy'],
  // potterybarnkids: ['external_registry_hint_potterybarnkids_fonsst'],
  // amazon: [
  //   'external_registry_hint_amazon_cergc3',
  //   'external_registry_hint_amazon_2_qltyeg',
  // ],
}

const STORE_MAP = {
  amazon: 'Amazon',
  babylist: 'Babylist',
  buybuybaby: 'buybuyBABY',
  potterybarnkids: 'Pottery Barn Kids',
  target: 'Target',
  walmart: 'Walmart',
  other: '+ Another Store',
}

class AddOtherRegistryPage extends Component {
  static propTypes = {
    currentUser: PropTypes.object,
    registryId: PropTypes.number.isRequired,
    registrySlug: PropTypes.string.isRequired,
    tracking: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)

    onCurrentUserLoaded((currentUser) => {
      props.tracking.trackEvent({
        event: track.pageViewed,
        eventUrl: window.location.href,
        registryOwnerId: currentUser?.id,
        registryId: currentUser?.registryId,
      })
    })
  }

  state = {
    addType: null,
    additionalInfo: null,
    formErrors: {},
    statusMessage: null,
    statusMessageVariant: null,
    selectedStore: null,
    showSubmitButton: true,
    showAddAnotherRegistryButton: false,
    showContactCSButton: false,
    showHintModal: false,
    submitting: false,
    transferKey: null,
  }

  render() {
    const { currentUser } = this.props
    const hintImgIds = STORE_HINT_CLOUDINARY_IMAGE_IDS[this.state.selectedStore]

    return (
      <div className={css.addOtherRegistryContainer}>
        <div className={css.innerWrapper}>
          <h1 className={css.title}>Transfer or Link Another Registry</h1>
          <div className={css.subHeader}>
            <img
              className={css.linkImage}
              src="//images.babylist.com/image/upload/registry-icons/add_other_registry.svg"
            />
            <div className={css.subHeaderText}>
              <h4>Already built a baby registry somewhere else?</h4>
              Don't lose all your hard work! Transfer your items from another
              registry, or just add a link.
            </div>
          </div>

          <div className={css.formSection}>
            <p>
              <b>Step 1:</b> How would you like to add your other registry?
            </p>
            <hr className="mts mbm" />
            <Radio
              checked={this.state.addType === TRANSFER}
              groupClassName={css.radioOption}
              name="addType"
              size="large"
              label="Transfer Items"
              onChange={() =>
                this.resetStatusMessageState({
                  addType: TRANSFER,
                })
              }
              help="Perfect if you only want to manage one registry. Transfer all your items from the other registry to your Babylist. Once the transfer is complete delete your other registry. 🎉"
            />

            <Radio
              checked={this.state.addType === LINK}
              groupClassName={css.radioOption}
              name="addType"
              size="large"
              label="Add Registry Link"
              onChange={() =>
                this.resetStatusMessageState({
                  addType: LINK,
                })
              }
              help={
                <span>
                  {`Perfect if you want to keep the other registry separate, but only want to share one registry. The other registry will appear as a link on your Babylist. The registries will not sync, `}
                  <b>so don't put the same items on both registries.</b>
                </span>
              }
            />
          </div>

          {this.state.addType ? (
            <Form
              className={css.formSection}
              onSubmit={this.handleSubmit.bind(this)}
            >
              <p>
                <b>Step 2:</b> Which store is your other registry with?
              </p>
              <hr className="mts mbm" />
              <div data-testid="select-store" style={{ maxWidth: 500 }}>
                <Select
                  inline={!bowserFlags.mobile}
                  label="Select a Store"
                  onChange={(e) =>
                    this.resetStatusMessageState({
                      selectedStore: e.target.value,
                    })
                  }
                  error={this.state.formErrors.selectedStore}
                  wrapperClassName={css.storeDropdownWrapper}
                >
                  <option />
                  {map(STORE_MAP, (val, key) => (
                    <option key={key} value={key}>
                      {val}
                    </option>
                  ))}
                </Select>

                {this.state.selectedStore &&
                has(
                  STORE_HINT_CLOUDINARY_IMAGE_IDS,
                  this.state.selectedStore
                ) ? (
                  <span className="pll h7 hidden-xs">
                    <Link
                      onClick={() => this.setState({ showHintModal: true })}
                      className={`${css.findRegistryLink} find-registry-link`}
                    >
                      {`Find your registry ${
                        this.usesRegistryNumber() ? 'number' : 'link'
                      }`}
                    </Link>
                  </span>
                ) : null}

                {this.usesRegistryNumber() ? (
                  <Input
                    label="Your registry number"
                    onChange={(e) =>
                      this.resetStatusMessageState({
                        otherRegistryNumber: this.normalizeRegistryNumber(
                          e.target.value
                        ),
                      })
                    }
                    error={this.state.formErrors.otherRegistryNumber}
                  />
                ) : (
                  <Input
                    label="Store's registry link"
                    onChange={(e) =>
                      this.resetStatusMessageState({
                        otherRegistryUrl: this.normalizeUrl(e.target.value),
                      })
                    }
                    error={this.state.formErrors.otherRegistryUrl}
                  />
                )}

                {this.state.selectedStore == 'other' ? (
                  <Input
                    className="additional-info"
                    type="textarea"
                    rows={4}
                    label="Additional info to help us find this registry"
                    onChange={(e) =>
                      this.resetStatusMessageState({
                        additionalInfo: e.target.value,
                      })
                    }
                    help="For example: your arrival date, partner's full name, the state you reside in..."
                  />
                ) : null}

                {this.state.statusMessage && (
                  <p>
                    {this.renderStatusIcon()} {this.state.statusMessage}
                  </p>
                )}

                <div className={css.buttonPanel} data-testid="button-panel">
                  {this.state.showSubmitButton && (
                    <InfoButton
                      pill
                      disabled={!!this.state.statusMessage}
                      type="submit"
                      submitting={this.state.submitting}
                      className={css.submitButton}
                    >
                      {this.state.addType == TRANSFER
                        ? this.state.submitting && this.state.transferKey
                          ? 'Transferring Items'
                          : 'Transfer Items'
                        : this.usesRegistryNumber()
                          ? 'Add Registry Number'
                          : 'Add Registry Link'}
                    </InfoButton>
                  )}

                  {this.state.showAddAnotherRegistryButton && (
                    <InfoButton
                      pill
                      empty
                      type="reset"
                      onClick={() => {
                        this.resetStatusMessageState({
                          addType: null,
                        })
                        Scroll.animateScroll.scrollToTop()
                      }}
                    >
                      Add Another Registry
                    </InfoButton>
                  )}

                  {this.state.showContactCSButton && (
                    <InfoButton
                      pill
                      empty
                      onClick={this.handleSubmitSupportTicket.bind(this)}
                    >
                      Contact Customer Support
                    </InfoButton>
                  )}
                </div>
              </div>
            </Form>
          ) : null}

          {this.state.showHintModal && (
            <Modal
              show={this.state.showHintModal}
              title={`Find Your Registry ${
                this.usesRegistryNumber() ? 'Number' : 'Link'
              }`}
              onHide={() => this.setState({ showHintModal: false })}
            >
              <ModalBody>
                {this.state.addType === TRANSFER ? (
                  <p>
                    Make sure to set your other registry's visibility to Public
                    so that we can copy all the items from your registry to
                    Babylist.
                  </p>
                ) : (
                  <p>
                    Make sure to set your other registry's visibility to Public
                    so that your friends and family can see the items in your
                    linked registry.
                  </p>
                )}
                {hintImgIds.length > 1 ? (
                  map(hintImgIds, (imgId, idx) => (
                    <div key={idx}>
                      <div className="h2">{`Step ${idx + 1}`}</div>
                      <Image
                        role="none"
                        className="img-responsive"
                        src={`//images.babylist.com/image/upload/${imgId}.jpg`}
                      />
                    </div>
                  ))
                ) : (
                  <Image
                    role="none"
                    className="img-responsive"
                    src={`//images.babylist.com/image/upload/${hintImgIds[0]}.jpg`}
                  />
                )}
              </ModalBody>
            </Modal>
          )}

          {/*
            For registry transfers, the poller will look for a result from
            the background job for 20 seconds and then go to the error state,
            which let's the user know they'll get an email
          */}
          <AsyncPoller
            giveUpAfter={20000}
            onComplete={this.handleTransferPollingEnd.bind(this)}
            onGiveUp={this.handleTransferPollingEnd.bind(this)}
            url={
              this.state.transferKey
                ? blUrls.apiV3AsyncJobsPath(this.state.transferKey)
                : null
            }
          />
        </div>
      </div>
    )
  }

  renderStatusIcon() {
    const iconDimension = 20
    switch (this.state.statusMessageVariant) {
      case VARIANTS.DANGER:
        return (
          <DangerIcon
            height={iconDimension}
            width={iconDimension}
            className="mrm"
          />
        )
      case VARIANTS.INFO:
        return (
          <InfoIcon
            height={iconDimension}
            width={iconDimension}
            className="mrm"
          />
        )
      case VARIANTS.SUCCESS:
        return (
          <SuccessIcon
            height={iconDimension}
            width={iconDimension}
            className="mrm"
          />
        )
      default:
        return <></>
    }
  }

  resetStatusMessageState(stateChangeParams) {
    this.setState({
      showAddAnotherRegistryButton: false,
      showContactCSButton: false,
      showSubmitButton: true,
      statusMessage: null,
      statusMessageVariant: null,
      ...stateChangeParams,
    })
  }

  usesRegistryNumber() {
    return false
  }

  isFormValid() {
    const formErrors = {
      selectedStore:
        !this.state.selectedStore || !this.state.selectedStore.length
          ? 'Select a store to get started'
          : null,
      otherRegistryUrl: this.state.otherRegistryUrl
        ? validateUrl(this.state.otherRegistryUrl)
        : 'Enter the link to your registry',
      otherRegistryNumber: this.state.otherRegistryNumber
        ? validateRegistryNumber(this.state.otherRegistryNumber)
        : 'Enter a valid registry number',
    }
    this.setState({ formErrors })
    return (
      !formErrors.selectedStore &&
      (this.usesRegistryNumber() || !formErrors.otherRegistryUrl) &&
      (!this.usesRegistryNumber() || !formErrors.otherRegistryNumber)
    )
  }

  normalizeUrl(url) {
    url = url.trim()
    if (url.indexOf('http://') != 0 && url.indexOf('https://') != 0) {
      url = `http://${url}`
    }
    return url
  }

  normalizeRegistryNumber(number) {
    return number.trim().replace(/[^\d]/g, '')
  }

  handleSubmitSupportTicket() {
    this.submitSupportTicket()
      .then(() => {
        this.setState({
          statusMessage:
            'We got your request! A Happiness Hero will contact you via email!',
          statusMessageVariant: VARIANTS.SUCCESS,
          showAddAnotherRegistryButton: true,
          showContactCSButton: false,
        })
      })
      .catch(() => {
        this.setState({
          statusMessage: `Something went wrong. Please contact ${SUPPORT_EMAIL} for more assistance.`,
          statusMessageVariant: VARIANTS.DANGER,
          showAddAnotherRegistryButton: true,
          showContactCSButton: false,
        })
      })
  }

  handleSubmit(e) {
    e.preventDefault()

    if (this.isFormValid()) {
      this.setState({ submitting: true })
      this.submitForm()
        .then((resp) => {
          if (this.state.addType == TRANSFER) {
            // don't show a success message or transition out of submitting state
            // we will now poll for when the transfer is completed or
            // the polling hits its limit we will show the success message then
            this.setState({
              transferKey: resp.jobKey,
              statusMessage: null,
              statusMessageVariant: null,
            })
          } else {
            // for links we can immediately show the message
            const message = (
              <span>
                {`Your ${
                  this.state.selectedStore === 'other'
                    ? ''
                    : `${STORE_MAP[this.state.selectedStore]} `
                }baby registry is now linked to your Babylist. `}
                <Link url={`/${this.props.registrySlug}`}>Check it out</Link>.
              </span>
            )

            this.setState({
              submitting: false,
              statusMessage: message,
              statusMessageVariant: VARIANTS.SUCCESS,
              showAddAnotherRegistryButton: true,
            })
          }
        })
        .catch((resp) => {
          const error = parseErrors(resp)
          const message = error[SYSTEM_ERROR_KEY]
          const stateChanges = {
            statusMessage: message,
            statusMessageVariant: VARIANTS.DANGER,
            showContactCSButton: true,
          }

          this.setState({
            submitting: false,
            ...stateChanges,
          })
        })
    }
  }

  handleTransferPollingEnd(resp) {
    let message
    let showContactCSButton = true
    let showSubmitButton = false
    let variant = VARIANTS.SUCCESS
    if (!resp) {
      // this means that the polling timed out.
      variant = VARIANTS.INFO
      message = (
        <span>
          This is taking longer than usual. You will receive an email when your{' '}
          {STORE_MAP[this.state.selectedStore]} registry has been successfully
          transferred to your Babylist.
        </span>
      )
    } else if (resp.error) {
      // there was an error automatically transferring the items
      variant = VARIANTS.DANGER
      message = (
        <span>
          {resp.message ||
            'We were unable to automatically complete your transfer request.'}
        </span>
      )
    } else {
      showSubmitButton = true
      showContactCSButton = false
      message = (
        <span>
          <Link url={`/${this.props.registrySlug}`}>Check it out!</Link> The
          items on your {STORE_MAP[this.state.selectedStore]} baby registry have
          been added to your Babylist. You can now safely{' '}
          <Link url={this.state.otherRegistryUrl} newWindow>
            delete your other registry
          </Link>
          .
        </span>
      )
    }

    this.setState({
      submitting: false,
      statusMessage: message,
      statusMessageVariant: variant,
      showAddAnotherRegistryButton: true,
      showContactCSButton,
      showSubmitButton,
    })
  }

  registryUrlForNumber() {
    return null
  }

  submitForm() {
    const path =
      this.state.addType == TRANSFER
        ? blUrls.apiV3TransferExternalRegistryPath
        : blUrls.apiV3LinkExternalRegistryPath

    const registryUrl = this.usesRegistryNumber()
      ? this.registryUrlForNumber()
      : this.state.otherRegistryUrl

    return fetch(path(this.props.registryId), {
      method: 'POST',
      body: JSON.stringify({
        registryId: this.props.registryId,
        store: this.state.selectedStore,
        externalRegistry: {
          url: registryUrl,
          note: this.state.additionalInfo,
        },
      }),
    })
  }

  submitSupportTicket() {
    const { currentUser, tracking, registryId } = this.props
    const {
      addType,
      selectedStore,
      otherRegistryUrl,
      additionalInfo,
      statusMessage,
    } = this.state

    const registryUrl = this.usesRegistryNumber()
      ? this.registryUrlForNumber()
      : otherRegistryUrl

    const path = apiV3ExternalRegistrySupportTicketPath(registryId)

    tracking.trackEvent({
      event: track.externalRegistryContactSupportClicked,
      eventType: track.EventType.CLICK,
      eventUrl: window.location.href,
      registryOwnerId: currentUser?.id,
      registryId: currentUser?.registryId,
      externalRegistryMergeType:
        addType === TRANSFER
          ? track.ExternalRegistryMergeType.TRANSFER
          : track.ExternalRegistryMergeType.LINK,
      externalRegistryUrl: registryUrl,
      externalStoreName: selectedStore,
      errorMessage: statusMessage,
    })

    return fetch(path, {
      method: 'POST',
      body: JSON.stringify({
        registryId,
        store: selectedStore,
        mergeType: addType,
        externalRegistry: {
          url: registryUrl,
          note: additionalInfo,
        },
      }),
    })
  }
}

export default withContextualizedTracking({
  eventLocation: track.EventLocation.EXTERNAL_REGISTRY_TRANSFER,
})(withCurrentUser(AddOtherRegistryPage))
